/** @jsx isolateComponent */
import { Fragment } from 'react';
import { css } from '@emotion/react';
import { isolateComponent, labelStyles } from '@zapier/style-encapsulation';
import {
  Button,
  Colors,
  Heading,
  HeadingLevel,
  Icon,
  IconProps,
  Link,
  Shadows,
  Typography,
} from '../../../index';

export type Props = {
  /**
   * Heading text content.
   */
  heading?: string;
  /**
   * Description text displayed below the heading.
   */
  description?: React.ReactNode;
  /**
   * Text content of the CTA button.
   */
  ctaText?: string;
  /**
   * href for the CTA button.
   */
  ctaHref?: string;
  /**
   * Text content of the secondary CTA button.
   */
  secondaryCtaText?: string;
  /**
   * href for the secondary CTA button.
   */
  secondaryCtaHref?: string;
  /**
   * If true makes the whole block clickable.
   */
  isLink?: boolean;
  /**
   * Centers content
   */
  isCentered?: boolean;
  /**
   * Media displayed above a heading.
   */
  media?: React.ReactNode;
  /**
   * Content text displayed below the description.
   */
  smallText?: string;
  /**
   * String representing the name of icon to display.
   */
  iconName?: IconProps['name'];
  /**
   * String representing the color of icon to display.
   */
  iconColor?: IconProps['color'];
  /**
   * CTA button click handler.
   */
  onCtaClick?: (e: React.MouseEvent<HTMLElement, MouseEvent>) => void;
  /**
   * CTA button keyDown handler.
   */
  onCtaKeyDown?: (e: React.KeyboardEvent<HTMLElement>) => void;
  /**
   * Secondary CTA button click handler.
   */
  onSecondaryCtaClick?: (e: React.MouseEvent<HTMLElement, MouseEvent>) => void;
  /**
   * Secondary CTA button keyDown handler.
   */
  onSecondaryCtaKeyDown?: (e: React.KeyboardEvent<HTMLElement>) => void;
};

type ContentProps = Pick<
  Props,
  'iconName' | 'iconColor' | 'media' | 'heading' | 'description' | 'smallText'
>;

type PrimaryCTAProps = Pick<
  Props,
  'ctaHref' | 'onCtaClick' | 'onCtaKeyDown' | 'ctaText'
>;

type SecondaryCTAProps = Pick<
  Props,
  | 'secondaryCtaHref'
  | 'onSecondaryCtaClick'
  | 'onSecondaryCtaKeyDown'
  | 'secondaryCtaText'
>;

const breakpoints = {
  tablet: 768,
  desktop: 1230,
};

const ContentStyles = {
  iconContainer: css`
    margin-bottom: 15px;
  `,
  mediaContainer: css`
    width: 100%;
    margin-bottom: 20px;
  `,
  heading: [
    css`
      margin-bottom: 10px;
      color: ${Colors.neutral800};
      ${Typography.pageHeader9}
    `,
  ],
  description: [
    css`
      margin-bottom: 10px;
      color: ${Colors.neutral800};
      ${Typography.paragraph3}

      @media screen and (min-width: ${breakpoints.tablet}px) {
        ${Typography.paragraph1Short}
      }
    `,
  ],
  smallText: [
    css`
      margin-bottom: 10px;
      color: ${Colors.neutral700};
      ${Typography.smallPrint1}

      @media screen and (min-width: ${breakpoints.tablet}px) {
        ${Typography.paragraph3}
      }
    `,
  ],
};

const PrimaryCTAStyles = {
  primaryCtaContainer: css`
    & > a,
    & > button {
      width: 100%;
    }

    @media screen and (min-width: ${breakpoints.tablet}px) {
      & > a,
      & > button {
        width: auto;
      }
    }
  `,
};
const SecondaryCTAStyles = {
  secondaryCtaContainer: css`
    margin-top: 10px;

    & > a {
      display: inline-flex;
      align-items: center;
    }
  `,
  secondaryCtaText: css`
    ${Typography.paragraph3Bold}
  `,
  secondaryCtaIconContainer: css`
    margin-left: 6px;
    height: 24px;
  `,
};

const Styles = labelStyles('Block', {
  container: (isCentered: boolean) => [
    css`
      background-color: transparent;
      display: flex;
      flex-direction: column;
      margin: 0 auto;
      align-items: ${isCentered ? 'center' : 'flex-start'};
      text-align: ${isCentered ? 'center' : 'left'};
    `,
  ],
  link: css`
    &:hover img {
      box-shadow: ${Shadows.elevation20};
    }
  `,
  ...ContentStyles,
  ...PrimaryCTAStyles,
  ...SecondaryCTAStyles,
});

const Content = ({
  iconName,
  iconColor,
  media,
  heading,
  description,
  smallText,
}: ContentProps) => {
  return (
    <Fragment>
      {iconName && (
        <div data-testid="icon" css={Styles.iconContainer}>
          <Icon color={iconColor} name={iconName} size={50} />
        </div>
      )}
      {media && <div css={Styles.mediaContainer}>{media}</div>}
      {heading && (
        <HeadingLevel>
          <Heading css={Styles.heading}>{heading}</Heading>
        </HeadingLevel>
      )}
      {description && <div css={Styles.description}>{description}</div>}
      {smallText && <p css={Styles.smallText}>{smallText}</p>}
    </Fragment>
  );
};

const PrimaryCTA = ({
  ctaHref,
  onCtaClick,
  onCtaKeyDown,
  ctaText,
}: PrimaryCTAProps) => (
  <div css={Styles.primaryCtaContainer}>
    <Button
      color="primary"
      href={ctaHref}
      onClick={onCtaClick}
      onKeyDown={onCtaKeyDown}
      data-testid="cta-button"
    >
      {ctaText}
    </Button>
  </div>
);

const SecondaryCTA = ({
  secondaryCtaHref,
  onSecondaryCtaClick,
  onSecondaryCtaKeyDown,
  secondaryCtaText,
}: SecondaryCTAProps) => {
  return (
    <div css={Styles.secondaryCtaContainer}>
      <Link
        data-testid="cta-secondary-button"
        href={secondaryCtaHref}
        onClick={onSecondaryCtaClick}
        onKeyDown={onSecondaryCtaKeyDown}
        weight="bold"
      >
        <div css={Styles.secondaryCtaText}>{secondaryCtaText}</div>
      </Link>
    </div>
  );
};

export function Block({
  isLink = false,
  isCentered = false,
  description,
  iconName,
  iconColor = 'blue',
  media,
  ctaText,
  ctaHref,
  secondaryCtaText,
  secondaryCtaHref,
  heading,
  smallText,
  onCtaClick,
  onCtaKeyDown,
  onSecondaryCtaClick,
  onSecondaryCtaKeyDown,
}: Props) {
  const hasPrimaryCta = Boolean(ctaText && (ctaHref || onCtaClick));
  const hasSecondaryCta = Boolean(
    secondaryCtaText && (secondaryCtaHref || onSecondaryCtaClick)
  );

  const contentProps: ContentProps = {
    iconName,
    iconColor,
    media,
    heading,
    description,
    smallText,
  };
  const primaryCTAProps: PrimaryCTAProps = {
    ctaHref,
    onCtaClick,
    onCtaKeyDown,
    ctaText,
  };
  const secondaryCTAProps: SecondaryCTAProps = {
    secondaryCtaHref,
    onSecondaryCtaClick,
    onSecondaryCtaKeyDown,
    secondaryCtaText,
  };

  return (
    <div css={Styles.container(isCentered)}>
      {isLink ? (
        <Link
          href={ctaHref}
          data-testid="link-wrapper"
          onClick={onCtaClick}
          onKeyDown={onCtaKeyDown}
          css={Styles.link}
        >
          <Content {...contentProps} />
        </Link>
      ) : (
        <Fragment>
          <Content {...contentProps} />
          {hasPrimaryCta && <PrimaryCTA {...primaryCTAProps} />}
          {hasSecondaryCta && <SecondaryCTA {...secondaryCTAProps} />}
        </Fragment>
      )}
    </div>
  );
}
